import GoogleProvider from "next-auth/providers/google";
import CredentialsProvider from "next-auth/providers/credentials";
import EmailProvider from "next-auth/providers/email";
import type { Provider } from "next-auth/providers/index";
import type { EnabledProviders } from "../@types";
import {
  login,
  loginByOTP,
  getWhiteLabelIdByCustomDomain,
  getWhiteLabelIdBySubdomain,
  WHITE_LABEL_REQUIRED_MESSAGE,
} from "database/services";
import { signUp } from "./signup";
import {
  sanitizeOptionalId,
  sanitizeString,
  resolveSignupPassword,
} from "./signup-utils";
import { User, OrderType } from "database";
import { getSubdomain } from "@sajilni/utils/getSubdomain";
import type { AuthRequestLike } from "./request-like";

export default function getEnabledProviders(
  enabledProviders: EnabledProviders,
  request?: AuthRequestLike
) {
  const providers: Provider[] = [];

  // Helper function to get WhiteLabel ID from request
  const getAccessibleWhiteLabelId = async (): Promise<string | undefined> => {
    if (!request) return undefined;

    const host = request.headers.get("host");
    if (!host) return undefined;

    const domain = host.replace(
      ".localhost:3000",
      `.${process.env["NEXT_PUBLIC_ROOT_DOMAIN"]}`
    );
    const subdomain = getSubdomain(domain);

    return subdomain
      ? ((await getWhiteLabelIdBySubdomain(subdomain)) ?? undefined)
      : ((await getWhiteLabelIdByCustomDomain(domain)) ?? undefined);
  };

  enabledProviders.forEach(({ provider, clientId, clientSecret }) => {
    switch (provider) {
      case "google":
        providers.push(
          GoogleProvider({
            clientId: clientId as string,
            clientSecret: clientSecret as string,
          })
        );
        break;

      case "credentials":
        providers.push(
          CredentialsProvider({
            name: "Credentials",
            credentials: {
              email: {
                label: "email",
                type: "text",
                placeholder: "Email",
              },
              password: {
                label: "password",
                type: "password",
                placeholder: "Password",
              },
              token: {
                label: "token",
                type: "text",
                placeholder: "Token",
              },
              rememberMe: {
                label: "rememberMe",
                type: "boolean",
                placeholder: "RememberMe",
              },
              eventSlug: { label: "Event Slug", type: "text" },
              isSignUp: { label: "isSignUp", type: "boolean" },
              eventId: { label: "Event Id", type: "text" },
              name: { label: "Name", type: "text" },
              orderId: { label: "Order Id", type: "text" },
              phoneNumber: { label: "Phone Number", type: "text" },
              profilePayload: { label: "Profile Payload", type: "text" },
              whiteLabelId: { label: "White Label Id", type: "text" },
              participantGroupId: {
                label: "Participant Group Id",
                type: "text",
              },
              orderType: { label: "Order Type", type: "text" },
            },
            async authorize(credentials) {
              // Handle sign-up branch with OTP generation and no session
              const isSignUp = credentials?.isSignUp === "true";

              if (isSignUp) {
                const profileDataRaw = credentials?.profilePayload;
                let profileData: Record<string, any> | undefined = undefined;
                try {
                  if (typeof profileDataRaw === "string") {
                    profileData = JSON.parse(profileDataRaw);
                  } else if (
                    profileDataRaw &&
                    typeof profileDataRaw === "object"
                  ) {
                    profileData = profileDataRaw as any;
                  }
                } catch (e) {
                  // ignore parse error; proceed without profile data
                }

                // if (!credentials?.eventId) {
                //   throw new Error("EVENT_ID_REQUIRED");
                // }

                const whiteLabelId =
                  sanitizeOptionalId(
                    credentials?.whiteLabelId as string | undefined
                  ) ?? (await getAccessibleWhiteLabelId());
                if (!whiteLabelId) {
                  throw new Error(WHITE_LABEL_REQUIRED_MESSAGE);
                }

                await signUp({
                  email: credentials?.email as string,
                  password: resolveSignupPassword(
                    credentials?.password as string | undefined
                  ),
                  name: sanitizeString(credentials?.name as string | undefined),
                  phoneNumber:
                    sanitizeString(credentials?.phoneNumber as string | undefined) ??
                    (profileData?.phone as string | undefined),
                  profileData,
                  eventId: sanitizeOptionalId(
                    credentials?.eventId as string | undefined
                  ),
                  orderId: sanitizeOptionalId(
                    credentials?.orderId as string | undefined
                  ),
                  whiteLabelId,
                  participantGroupId: sanitizeOptionalId(
                    credentials?.participantGroupId as string | undefined
                  ),
                  orderType: sanitizeString(
                    credentials?.orderType as string | undefined
                  ) as OrderType | undefined,
                });

                // Create OTP for the identifier (email preferred)
                // await generateOTP(credentials?.email as string);

                // Signal to client that OTP is required, without creating a session
                throw new Error("OTP_REQUIRED");
              }

              // Default sign-in flow
              // Get the accessible WhiteLabel ID from the request
              const accessibleWhiteLabelId = await getAccessibleWhiteLabelId();

              const user = await login(
                credentials?.email as string,
                credentials?.password,
                credentials?.token,
                accessibleWhiteLabelId
              );
              if (user.error) {
                throw new Error(user.error);
              }
              if (user) {
                return {
                  ...user,
                  rememberMe: credentials?.rememberMe === "true", // Convert to boolean
                  eventSlug: credentials?.eventSlug,
                } as User & { rememberMe: boolean; eventSlug?: string };
              }
              return null;
            },
          })
        );
        break;
      default:
        throw new Error("Unknown provider");
    }
  });

  // Add custom OTP verification provider
  // This provider is always available for OTP verification
  providers.push(
    CredentialsProvider({
      id: "otp-verification", // Custom ID to differentiate from regular credentials
      name: "OTP Verification",
      credentials: {
        email: {
          label: "email",
          type: "text",
          placeholder: "Email",
        },
        otp: {
          label: "otp",
          type: "text",
          placeholder: "OTP Code",
        },
        eventSlug: { label: "Event Slug", type: "text" },
        eventId: { label: "Event Id", type: "text" },
        whiteLabelId: { label: "White Label Id", type: "text" },
      },
      async authorize(credentials) {
        const email = credentials?.email;
        const otp = credentials?.otp;
        const eventId = sanitizeOptionalId(
          credentials?.eventId as string | undefined
        );
        const whiteLabelId = sanitizeOptionalId(
          credentials?.whiteLabelId as string | undefined
        );
        if (!email || !otp) {
          throw new Error("EMAIL_AND_OTP_REQUIRED");
        }

        const user = await loginByOTP(
          email,
          Number(otp),
          eventId,
          whiteLabelId
        );
        if (!user || "error" in user) {
          throw new Error(
            user && "error" in user && user.error === "OTP_EXPIRED"
              ? "OTP_EXPIRED"
              : "INVALID_OTP"
          );
        }
        if (user)
          // 5. Return the user object to create session
          return {
            id: user.id,
            email: user.email,
            name: user.name,
            eventSlug: credentials?.eventSlug,
          } as User & { eventSlug?: string };

        // PLACEHOLDER: Replace this with your actual verification logic
        // For now, returning null will fail authentication
        // After you implement the logic, return the user object
        return null;
      },
    })
  );

  return providers;
}
